import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Grid, Box, Link, Typography, Switch } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { useAuth } from 'hooks/useAuth';
import { snackAlertData } from 'utils/constants';
import { integrationModalStyles } from '../style/style';
import { identifyUser, trackEvent } from 'utils/mixpanel';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { handleSuccessAlert } from 'utils/handleSuccessAlert';
import { skyBoxModalTutorials } from '../utils/SkyboxTutorials';
import { useSkyboxService } from 'store/services/skybox.service';
import JoyrideTour from 'components/Onboarding/Tour/JoyRideTour';
import { Modal, TextField, SnackBarAlert, CircleLoading, Button } from 'shared';
import { selectTutorialPreference, selectIsSkipped, completeTour } from 'store/slices/tour.slice';

export const SkyBoxModal = ({
    open,
    tourId,
    tourRun,
    onSuccess,
    isConnected,
    currentStep,
    handleCloseModal,
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user: { data: userData } } = useAuth();
    const classes = integrationModalStyles();
    const companyData = useSelector((state) => state.company.data);
    const isSkipped = useSelector((state) => selectIsSkipped(state, tourId));
    const tutorialPreference = useSelector((state) => selectTutorialPreference(state));
    const { getSkyboxToken, addSkyboxToken, updateSkyboxToken, updateSkyboxInvoiceStatus } =
        useSkyboxService();

    const [runTour, setRunTour] = useState(false);
    const [apiToken, setApiToken] = useState(null);
    const [accountId, setAccountId] = useState(null);
    const [loading, setLoading] = useState(isConnected);
    const [buttonMode, setButtonMode] = useState('add');
    const [selectedDate, setSelectedDate] = useState(null);
    const [originalToken, setOriginalToken] = useState('');
    const [buttonLoading, setButtonLoading] = useState(false);
    const [alertData, setAlertData] = useState(snackAlertData);
    const [updateInvoices, setUpdateInvoices] = useState(false);

    const handleSubmit = async () => {
        try {
            setButtonLoading(true);
            const data = {
                apiToken,
                companyId: companyData.id,
                startDate: selectedDate ? selectedDate.format('YYYY-MM-DD') : null,
                accountId,
                updateInvoices,
            };

            const response =
                buttonMode === 'add' ? await addSkyboxToken(data) : await updateSkyboxToken(data);

            onSuccess(response?.message);

            setTimeout(() => {
                navigate('/purchases/purchase-activity');
            }, 1000);
        } catch (error) {
            const errorMessage = error?.response?.data?.error;
            handleErrorAlert(
                setAlertData,
                errorMessage == 'Invalid token or account id.'
                    ? errorMessage
                    : 'Something went wrong, please try again',
            );
        } finally {
            setButtonLoading(false);
        }
    };

    const fetchToken = async () => {
        try {
            setLoading(true);
            const response = await getSkyboxToken(companyData.id);
            const { account_id, start_date, update_invoices, token } = response?.data;
            if (token) {
                setApiToken(token);
                setButtonMode('update');
                setOriginalToken(token);
                setAccountId(account_id);
                setUpdateInvoices(update_invoices);
                setSelectedDate(dayjs(start_date));
            } else {
                setButtonMode('add');
            }
        } catch (error) {
            setButtonMode('add');
        } finally {
            setLoading(false);
        }
    };

    const handleTourFinish = (isCompleted = null) => {
        setRunTour(false);

        if (tutorialPreference === 'skipped' || isSkipped) {
            // dispatch(completeTour({ tourId, skipSaving: false }));
            // return;
        }
        if (isCompleted) {
            dispatch(completeTour({ tourId, skipSaving: false }));
        }
    };

    const handleUpdateSkyboxInvoicesStatus = async (updateInvoices) => {
        try {
            const data = { companyId: companyData.id, updateInvoices };
            const response = await updateSkyboxInvoiceStatus(data);
            handleSuccessAlert(setAlertData, response?.message);

            identifyUser(userData.email.toLowerCase(), { $email: userData.email });
            trackEvent('POS Connected', {
                source: "Skybox",
            });
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const handleUpdateInvoicesChange = (event) => {
        const value = event.target.checked;
        setUpdateInvoices(value);
        if (buttonMode == 'update') {
            handleUpdateSkyboxInvoicesStatus(value);
        }
    };

    useEffect(() => {
        if (open && companyData?.id && isConnected) {
            fetchToken();
        }
    }, [open, companyData?.id]);

    useEffect(() => {
        if (open && tourRun) {
            if (currentStep < 2) {
                setTimeout(() => setRunTour(true), 1000);
            }
        }
    }, [open, currentStep, tourRun]);

    return (
        <Modal
            fullWidth
            open={open}
            title={`${buttonMode === 'update' ? 'Update' : 'Add'} Skybox Token`}
            handleClose={() => {
                handleCloseModal();
                handleTourFinish(true);
            }}
            actionBtnTitle={<Typography>{buttonMode === 'update' ? 'Update' : 'Add'}</Typography>}
        >
            <JoyrideTour
                steps={skyBoxModalTutorials}
                run={runTour}
                continuous={true}
                onFinish={handleTourFinish}
                showSkipButton={true}
                showProgress={true}
                stepIndex={0}
                isModal={true}
                showBeacon={true}
                spotlightClicks={true}
                callback={(event) => {
                    if (event.action === 'skip') {
                        setRunTour(false);
                        dispatch(completeTour({ tourId, skipSaving: false }));
                        handleTourFinish();
                    }
                }}
            />
            <>
                <Typography variant="body2" marginBottom={2} fontWeight="bold">
                    To generate an API token, please follow the guide
                    <Link
                        href="https://skybox.zendesk.com/hc/en-us/articles/6769735238043-Getting-Started-with-Skybox-APIs"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-tour="skybox-token-link"
                        sx={{ ml: 0.5 }}
                    >
                        here
                    </Link>
                    .
                </Typography>

                {loading ? (
                    <CircleLoading />
                ) : (
                    <>
                        {buttonMode === 'update' && originalToken != apiToken && (
                            <Typography variant="body2" color="error" marginBottom={2}>
                                Warning: Updating your Skybox token or account id will prevent
                                fetching data from your previously connected token.
                            </Typography>
                        )}

                        <Grid container spacing={2} data-tour="skybox-token-fields">
                            <Grid item xs={12} md={6}>
                                <TextField
                                    inlineLabel="API Token"
                                    value={apiToken || ''}
                                    onChange={(e) => setApiToken(e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    placeholder="Enter your SkyBox API Token"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    inlineLabel="Account ID"
                                    value={accountId || ''}
                                    onChange={(e) => setAccountId(e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    placeholder="Enter your Account ID"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box data-tour="skybox-start-date">
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            sx={classes.datePicker}
                                            label="Select Start Date"
                                            value={selectedDate}
                                            onChange={(newValue) => setSelectedDate(newValue)}
                                            disabled={
                                                buttonMode === 'update' &&
                                                apiToken === originalToken
                                            }
                                            minDate={dayjs().startOf('year')}
                                            maxDate={dayjs().endOf('year')}
                                            renderInput={(params) => (
                                                <TextField {...params} fullWidth sx={{ mb: 3 }} />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box display="flex" alignItems="center">
                                    <Switch
                                        checked={updateInvoices}
                                        onChange={handleUpdateInvoicesChange}
                                    />
                                    <Typography ml={1}>Update Invoices</Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        <Box mt={2} display="flex" justifyContent="flex-end">
                            <Button
                                onClick={handleSubmit}
                                variant="contained"
                                loading={buttonLoading}
                                disabled={!apiToken || !selectedDate || originalToken == apiToken}
                                text={buttonMode === 'update' ? 'Update Token' : 'Add Token'}
                                data-tour="skybox-submit-button"
                            />
                        </Box>

                        <Box data-tour="end-skybox-tutorial"></Box>
                    </>
                )}

                <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />
            </>
        </Modal>
    );
};

export default SkyBoxModal;
